/* eslint-disable react/display-name */
import React, { MouseEvent } from 'react';
import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    TimeScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    Title,
    defaults,
    SubTitle,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import { LoadingDots } from '@app/ui';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import addDataLabels from './addDataLabels';
import addCanvasBackground from './addCanvasBackground';

defaults.font.family = 'aspiralight, pnlight';
ChartJS.register(
    LinearScale,
    CategoryScale,
    TimeScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    Title,
    ChartDataLabels,
    addCanvasBackground,
    SubTitle,
);

import * as R from 'ramda';

// Graph Config section
import baseConfig from '../../utils/charts/base-config';
import mergeConfigs from '../../utils/charts/merge-configs';

import decimalY from '../../utils/charts/decimal-y-axis-config';
import categoryY from '../../utils/charts/category-y-axis-config';
import secY from '../../utils/charts/sec-y-axis-config';
import percentageY from '../../utils/charts/percentage-y-axis-config';
import currencyY from '../../utils/charts/currency-y-axis-config';
import dateY from '../../utils/charts/date-y-axis-config';
import doubleDecimalY from '../../utils/charts/double-decimal-y-axis-config';
import decimalStackedY from '../../utils/charts/decimal-stacked-y-axis-config';

import categoryX from '../../utils/charts/category-x-axis-config';
import decimalX from '../../utils/charts/decimal-x-axis-config';
import currencyX from '../../utils/charts/currency-x-axis-config';
import dateX from '../../utils/charts/date-x-axis-config';
import categoryStackedX from '../../utils/charts/category-stacked-x-axis-config';

const yAxisConfigs: {
    [key: string]: (props: any) => any;
} = {
    decimal: decimalY,
    category: categoryY,
    percentage: percentageY,
    sec: secY,
    currency: currencyY,
    date: dateY,
    doubleDecimal: doubleDecimalY,
    decimalStacked: decimalStackedY,
};

const xAxisConfigs = {
    category: categoryX,
    decimal: decimalX,
    currency: currencyX,
    date: dateX,
    categoryStacked: categoryStackedX,
};

const strategy = (key, left, right) => {
    return right;
};

const barDefault = color => ({
    fill: false,
    backgroundColor: color,
    borderColor: color,
    borderWidth: 1.5,
    borderDashOffset: 0.0,
    borderJoinStyle: 'miter',
    pointBorderColor: '#fff',
    pointBackgroundColor: color,
    pointBorderWidth: 1,
    pointHoverRadius: 2,
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: color,
    pointHoverBorderWidth: 2,
    pointRadius: 3,
    pointHitRadius: 10,
});

const mapIndexed = R.addIndex(R.map);
// eslint-disable-next-line no-unused-vars
const appendColor = mapIndexed(set => {
    return R.merge(barDefault(set.color), set);
});

export type BarChartOptionProps = {
    config?: any;
    currency?: string;
    title?: string;
    isDoubled?: boolean;
    isStacked?: boolean;
    xType:
        | 'currency'
        | 'percentage'
        | 'decimal'
        | 'date'
        | 'category'
        | 'categoryStacked';
    yType:
        | 'currency'
        | 'percentage'
        | 'decimal'
        | 'doubleDecimal'
        | 'date'
        | 'category'
        | 'sec'
        | 'decimalStacked';
    plugins?: any;
};

export type BarChartProps = {
    data: {
        datasets: { data: string[] | number[] }[];
        labels: string[][] | string[] | number[];
    };
    isLoading?: boolean;
    options?: BarChartOptionProps;
    labelOptions?: {
        showValues?: boolean;
        showPercentage?: boolean;
        showFixedAbbreviatedDecimal?: boolean;
        showItemValueAsPercentage?: boolean;
        appendPercentage?: boolean;
        customNoDataLabel?: string;
    };
    plugins?: any;
    onClickCallback?: (event: MouseEvent<HTMLCanvasElement>) => void;
};

const BarChart = React.forwardRef(
    (
        {
            data,
            isLoading,
            options = { xType: null, yType: null },
            labelOptions = {},
            plugins = {},
            onClickCallback,
        }: BarChartProps,
        ref: any,
    ) => {
        const { labels } = data;
        // eslint-disable-next-line no-unused-vars
        const {
            currency = 'USD',
            xType,
            yType,
            title,
            config: optionConfig = {},
            isDoubled = false,
            isStacked = false,
        } = options;

        const {
            showValues,
            showPercentage,
            showItemValueAsPercentage,
            appendPercentage,
            showFixedAbbreviatedDecimal,
            customNoDataLabel,
        } = labelOptions;

        const { datasets } = data;
        const modifiedData = { ...data };

        if (showValues) {
            modifiedData.datasets = addDataLabels(datasets, {
                showPercentage,
                showItemValueAsPercentage,
                isDoubled,
                isStacked,
                showFixedAbbreviatedDecimal,
                customNoDataLabel,
                appendPercentage,
            });
        }

        const config = {
            ...optionConfig,
            showValues,
        };

        const chartConfig = mergeConfigs(
            strategy,
            baseConfig,
            xAxisConfigs[xType]({ currency, labels }),
            yAxisConfigs[yType]({ currency, labels }),
            title ? { title: { text: title } } : {},
            config,
        );

        const parsedData = R.evolve({ datasets: appendColor }, modifiedData);

        return isLoading ? (
            <LoadingDots size="lg" />
        ) : (
            <Bar
                ref={ref}
                data={parsedData}
                options={chartConfig}
                redraw
                plugins={plugins}
                onClick={onClickCallback}
            />
        );
    },
);

export default BarChart;
